<template>
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :alertType="alertType"
    :closable="alertClosable"
    :alertIcon="alertIcon"
    @onClose="showAlert = false"
  />
  <div class="container" data-testid="virtual-card-details">
    <div v-if="displayCioSpaceOn">
      <div
        :id="inlineMessageHtmlId"
        class="mx-auto d-flex flex-column justify-content-between"
      ></div>
      <div
        id="cl-inline-message-container-virtual-card-details-general"
        class="mx-auto d-flex flex-column justify-content-between"
      ></div>
      <hr class="mt-3 mb-1 p-0" />
    </div>
    <DisclaimerBanner
      class="mb-3"
      v-if="payjoyCardStatus !== PayJoyCardStatus.ACTIVE && delinquent"
    >
      <p
        class="text-black mx-auto m-0"
        v-html="t('PAYJOY_CARD_VIEWS.DELINQUENT_DISCLAIMER')"
      ></p>
    </DisclaimerBanner>

    <CardDisplay
      v-if="payjoyCardStatus"
      :cardStatus="payjoyCardStatus"
      :iFrameUrl="iFrameUrl"
      :showDetails="showDetails"
    />

    <hr class="mt-3 mb-1 p-0" />

    <CardActions
      :showCreate="showCreate"
      :showToggle="showToggle"
      :showDelete="showDelete"
      :disableCreate="disableCreate"
      :disableCard="disableCard"
      :showWalletBtn="showWalletBtn"
      :showDetails="showDetails"
      :addToWalletAsset="addToWalletAsset || ''"
      :walletBtnType="walletBtnType || 'none'"
      @createCard="createVirtualCard"
      @toggleCard="handleCardStatusToggle"
      @toggleDetails="toggleCardDetails"
      @deleteCard="deleteCard"
      @navToCalculator="navToCalculator"
      @navToCardIntro="navToCardIntro"
      @addToWallet="addToWallet"
    />

    <hr class="mt-1 mb-3 p-0" />
  </div>

  <AddressConfirmationModal
    v-model:show="confirmModalDisplayed"
    :payjoyCardStatus="payjoyCardStatus"
    :addressInfo="addressInfo"
    :shipmentId="shipmentId"
    @close="closeModal"
  />

  <DownUpModal
    v-if="showVirtualCardCreationNotAllowedModal"
    :show-actions="false"
    :modal-header-image="redWarningIcon"
    :modal-header-title="t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.TITLE')"
    :modal-header-subtitle="
      t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.DESCRIPTION')
    "
    @click-overlay="closeVirtualCardCreationNotAllowedModal"
  />
</template>

<script lang="ts" setup>
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Alert from "@/shared-components/common/Alert.vue";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import DisclaimerBanner from "@/apps/credit-line/components/DisclaimerBanner.vue";
import AddressConfirmationModal from "@/apps/credit-line/components/CreditCard/VirtualCardDetails/AddressConfirmationModal.vue";
import CardActions from "@/apps/credit-line/components/CreditCard/VirtualCardDetails/CardActions.vue";
import CardDisplay from "@/apps/credit-line/components/CreditCard/VirtualCardDetails/CardDisplay.vue";
import { useVirtualCardDetails } from "@/apps/credit-line/hooks/useVirtualCardDetails";
import { computed, inject, ref, onMounted, onBeforeMount } from "vue";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { LogType } from "@/types/dataDogLogType";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { cioanalytics } from "@/apps/credit-line/services/customerio.service";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";

const { t, locale } = useI18n();

declare const Android: AndroidInterface;

// Injected property defined in CreditCardDetailsView.vue
const supportsAddToGoogleWallet = inject(
  "googleWalletEnabledForVirtualCard",
  ref(false)
);

const {
  payjoyCardStatus,
  disableCard,
  showAlert,
  alertMessage,
  alertIcon,
  alertType,
  alertClosable,
  delinquent,
  iFrameUrl,
  showCreate,
  showToggle,
  showDelete,
  disableCreate,
  showDetails,
  confirmModalDisplayed,
  addressInfo,
  shipmentId,
  createVirtualCard,
  toggleCardDetails,
  deleteCard,
  navToCalculator,
  navToCardIntro,
  closeModal,
  handleCardStatusToggle,
  addToWalletAsset,
  walletBtnType,
  addToWallet,
  showWalletBtn,
  showVirtualCardCreationNotAllowedModal,
  redWarningIcon,
  closeVirtualCardCreationNotAllowedModal,
} = useVirtualCardDetails(locale, supportsAddToGoogleWallet);

const hideNav = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

const displayCioSpaceOn = computed(() => showCreate || showDetails);

const cioSpace = computed(() =>
  payjoyCardStatus?.value ? payjoyCardStatus?.value?.toLowerCase() : null
);
const inlineMessageHtmlId = computed(() =>
  cioSpace.value
    ? "cl-inline-message-container-virtual-card-details-" + cioSpace.value
    : "cl-inline-message-container-virtual-card-details"
);

onMounted(() => {
  hideNav();
});

onBeforeMount(() => {
  const deviceTag = ref<string>(AuthenticationHelper.getDeviceTag());

  if (displayCioSpaceOn.value) {
    DatadogLogger.Log(
      "CustomerIo identify called on Virtual Card Details screen",
      {
        space: cioSpace.value,
        deviceTag: deviceTag.value,
      },
      LogType.INFO
    );
    cioanalytics?.identify(deviceTag.value);
    cioanalytics?.track(
      "creditline virtual card details screen " + cioSpace.value
    );
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

#card {
  overflow: hidden;
  gap: 10px;
  align-self: stretch;
  padding-left: 15px;
  padding-top: 25px;
  padding-bottom: 3rem;
}

#submit-btn {
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 44px;
  margin: 0 auto;
}

#pc-not-received-btn {
  position: fixed;
  bottom: -2%;
  left: 0;
  font-weight: bold;
  right: 0;
  color: $pj-color-green;
  width: 85%;
  margin: 2rem auto;
}

.green-label {
  color: $pj-color-green;
  font-size: 14px;
}

#modal-btn-cta {
  width: 45%;
  border-radius: 10px;
  border: 1px solid var(--main-primary-500, #2c7040);
  background: var(--main-white, #fff);
  color: var(--main-primary-500, #2c7040);
  height: 44px;
  padding: 10px 24px;
  text-align: center;
  font-size: medium;
}

.fill-txt {
  flex: 1 1 50% !important;
}

.modal-btn {
  width: 45%;
  border-radius: 10px;
  background: var(--main-primary-500, #2c7040);
  color: var(--main-white, #fff);
  height: 44px;
  text-align: center;
  font-size: medium;
  border: none;
}

a {
  color: $pj-color-green;
}
</style>
